// @kapowx/layout-grammar — the THIN THREE ADAPTER: a catalogue socket on a placed fixture → world slots.
//
// ASK-151 §6.7 ruling 1: ONE layout implementation, two consumers. The kapowx composer (`engine/slot-layouts/
// grammar.js`) and the GCG console's reviewer card both seat products through THIS file, so the reviewer sees
// a sample comic exactly where the shop will put it. Moved here from kapowx's composer adapter (ASK-154 seam 2)
// with the maths byte-for-byte — the §13.15 / §14.17 digest table in kapowx `scripts/test-layout-grammar.mjs`
// is the proof, and `test/three-adapter.test.mjs` re-proves two of its digests from inside the package.
//
// 🔴 THREE IS INJECTED, NEVER IMPORTED. `createThreeAdapter(THREE)` binds the caller's own three: kapowx passes
// its vendored module, the console its npm `three`. A package that imported `three` itself would put a SECOND
// three in whichever page did not match its version — two `Vector3` classes, `instanceof` lying, and a
// renderer that cannot draw the other copy's objects. The package gate (`test/package.test.mjs`) refuses
// any import here that is not a sibling.
//
// What it does NOT do: compose a fixture body, load a GLB or build a mesh. A slot is a pose; the caller
// clones whatever it draws there. That keeps the console's inspector and the shop on one placement while each
// keeps its own scene graph.
//
// ⚠️ The domain must be composed exactly as the BODY was, not merely by the anchor's yaw: the composer offsets
// the catalogue clone by (anchor.position − catalogue bbox-bottom-centre) and then yaws it about the anchor by
// `bodyYaw = (π − facingRad) + anchor.rotationY`. A consumer that seats sample products on its own preview body
// passes the SAME `catalogueFrame` it gave that body — `{ offset, pivot, yaw }` — or none, for a fixture whose
// body sits at the anchor unturned.

import { layoutByGrammar, surfaceLocalFrame } from "./grammar.js";
import { browseOf, slotBrowse } from "./browse.js";

const XYZ = (v) => ({ x: v.x, y: v.y, z: v.z });

/**
 * The fixture's OWN front, in CATALOGUE-LOCAL axes (ASK-147 §13.14 item 1).
 *
 * The placement contract's front (`attachment.clearance.front`) is catalogue-local +Z for every holder
 * manifest that declares no facing — the wall racks, the book bay, the crate, the tables. The box family is
 * the one exception, and it says so in its own data: a manifest that declares `defaults.facingDeg` (the
 * longbox's 180) is authored front-at-−X, which is exactly what the composer's `bodyYaw = π − facingRad`
 * assumes (it leaves a facing-180 body unturned). So the front is read from what the FIXTURE declares,
 * never from the composer's `facingDeg ?? 180` default, which a manifest that declares nothing never meant.
 */
export function fixtureLocalFront(manifest) {
	return Number.isFinite(manifest?.defaults?.facingDeg) ? { x: -1, y: 0, z: 0 } : { x: 0, y: 0, z: 1 };
}

/**
 * A socket's OWN front, in CATALOGUE-LOCAL axes, when it publishes a frame (ASK-147 §14.10) — else null.
 *
 * A socket that publishes origin + normal + uAxis (§14.4's socket-frame shape: H4 record-browser's six bins,
 * the new-arrivals-bin) carries its own forward: socket-local +Z = uAxis × normal, horizontalised (the lean is
 * the frame's business, the aisle is horizontal). A double-sided island's B bins (`yaw(π)·tiltX(−10°)`) face
 * B's aisle, not the fixture's single front. All three fields or none — the same test as `resolveDomain`'s
 * volume: a tilted spline publishing only `normal` (H2's tiers) is no frame and keeps the fixture rule.
 * Precedent: Unreal/Unity sockets carry their own transform; a socket's forward is its own, never the actor's.
 */
export function socketLocalFront(socket) {
	const g = socket?.geometry;
	const finite = (v) => v && [v.x, v.y, v.z].every(Number.isFinite);
	if (!finite(g?.origin) || !finite(g?.normal) || !finite(g?.uAxis)) return null;
	const u = g.uAxis, n = g.normal;
	const x = u.y * n.z - u.z * n.y, z = u.x * n.y - u.y * n.x;     // (u × n), y dropped: horizontalised
	const len = Math.hypot(x, z);
	if (!(len > 1e-6)) return null;                                     // a frame facing straight up/down has no aisle
	return { x: x / len + 0, y: 0, z: z / len + 0 };                    // + 0 folds −0, so a +Z front is main's bytes
}

/**
 * Which of these products the MERCHANT placed, and where — read from the feed's own assignment rows
 * (`via: 'pin'` + the parametric `at`, ASK-120 S2), never inferred. This is the whole activation
 * switch for free placement: until GCG's feed carries provenance every row reads `via: null`, this
 * returns [], and the region packs exactly as it does today (D-061 additive-and-inert).
 *
 * `at` is passed to the renderer VERBATIM. GCG stores and feeds it without resolving it (D-061 §5);
 * the client is the only thing that turns it into a position, which is why that happens in the pure
 * grammar and not here.
 */
export function pinsFor({ products, anchorName, socketId }) {
	if (!anchorName || !Array.isArray(products)) return [];
	const out = [];
	products.forEach((p, index) => {
		const rows = Array.isArray(p?.fixtureAssignments) ? p.fixtureAssignments : [];
		const hit = rows.find((a) => a && a.fixtureAnchor === anchorName
			&& (a.socketId == null || socketId == null || a.socketId === socketId)
			&& a.via === "pin" && a.at != null);
		if (hit) out.push({ index, at: hit.at });
	});
	return out;
}

/**
 * Bind the adapter to the caller's THREE — `{ Vector3, Quaternion, Matrix4 }`, the only three classes it uses (the
 * namespace works too). A bundled consumer passes the three CLASSES, not `import * as THREE`: a namespace handed
 * over as a value cannot be tree-shaken, and kapowx measured that at +6.6 KB of customer-initial JS.
 * Returns the world-space half of the grammar:
 *   resolveDomain · grammarApproach · quaternionFromBasis · seatSocket
 * plus the THREE-free helpers above, re-exposed so a consumer needs one handle.
 */
export function createThreeAdapter(THREE) {
	if (!THREE?.Vector3 || !THREE?.Quaternion || !THREE?.Matrix4) throw new Error("createThreeAdapter: pass three.js's { Vector3, Quaternion, Matrix4 } (or the module namespace)");
	const V3 = (p) => new THREE.Vector3(p.x, p.y, p.z);
	const Y = new THREE.Vector3(0, 1, 0);

	/** Catalogue-local point → world, mirroring the composer's body transform. */
	function makeToWorld({ anchor, catalogueFrame }) {
		const offset = catalogueFrame?.offset ? V3(catalogueFrame.offset) : V3(anchor.position);
		const pivot  = catalogueFrame?.pivot ? V3(catalogueFrame.pivot) : V3(anchor.position);
		const yaw    = Number.isFinite(catalogueFrame?.yaw) ? catalogueFrame.yaw : (anchor.rotationY || 0);
		const rotQ   = new THREE.Quaternion().setFromAxisAngle(Y, yaw);
		return (p) => V3(p).add(offset).sub(pivot).applyQuaternion(rotQ).add(pivot);
	}

	/** Catalogue-local DIRECTION → world. The body transform's rotation only: a direction has no
	 *  position, and translating one is how an axis ends up pointing at the origin. */
	function makeDirToWorld({ anchor, catalogueFrame }) {
		const yaw  = Number.isFinite(catalogueFrame?.yaw) ? catalogueFrame.yaw : (anchor.rotationY || 0);
		const rotQ = new THREE.Quaternion().setFromAxisAngle(Y, yaw);
		return (d) => V3(d).applyQuaternion(rotQ);
	}

	/**
	 * The direction a grammar socket is approached from, in WORLD: the socket's own front when it publishes a frame
	 * (§14.10), otherwise the fixture's own front,
	 * turned by the body's own yaw — the same transform `makeDirToWorld` gives the domain, so the approach can
	 * never disagree with where the shelf actually is. ONE rule for spline, surface, volume and point (F-139an
	 * applied it to volumes first); a fixture turned 90°, by its anchor or by a facing override, turns its
	 * approach with it.
	 */
	function grammarApproach({ manifest, anchor, catalogueFrame, socket = null }) {
		return makeDirToWorld({ anchor, catalogueFrame })(socketLocalFront(socket) || fixtureLocalFront(manifest));
	}

	/** The socket's domain, in world metres. `frontLocal` is the fixture's own front in CATALOGUE-LOCAL axes
	 *  (`fixtureLocalFront`) — the general-ring surface frame is derived in that frame, so it takes a local hint. */
	function resolveDomain({ socket, anchor, catalogueFrame, frontLocal = null }) {
		const toWorld = makeToWorld({ anchor, catalogueFrame });
		const dirToWorld = makeDirToWorld({ anchor, catalogueFrame });
		const kind = socket?.domain;
		if (kind === "spline") {
			const pts = (socket?.geometry?.points || []).map((p) => toWorld(p));
			if (pts.length < 2) return null;
			// ASK-147 §12.5 — the mint's lean (`geometry.normal`, fixture frame, published only past 1°) goes to
			// world with the body's own rotation. Absent stays absent: a level spline carries no normal field.
			const gn = socket?.geometry?.normal;
			const hasNormal = gn && [gn.x, gn.y, gn.z].every(Number.isFinite);
			return hasNormal ? { kind, points: pts.map(XYZ), normal: XYZ(dirToWorld(gn)) } : { kind, points: pts.map(XYZ) };
		}
		if (kind === "surface") {
			// ── THE MINT'S SHAPE (measured on live boot `ca6ca8e`, 2026-09-01 — 5 kinds emit it):
			//      geometry: { plane, cell, region:{xMin,xMax,zMin,zMax}, blocked:[…], regionExact }
			//    An axis-aligned rectangle at height `plane`, minus holes, all in FIXTURE-LOCAL metres.
			//
			//    Which means `{u,v}` needs no projection and no choice: the mint measures in local X/Z
			//    and the address IS local X/Z (INTERFACE 2026-08-26 — "metres in the socket's own local
			//    frame, the same frame the geometry already uses"). So u = local +X, v = local +Z, and
			//    `region.xMin` is literally the smallest legal `u`. The only work is rotating that frame
			//    into world with the SAME transform the composer gave the body — which is why the axes
			//    are rotated here rather than re-derived from world geometry, where a turned fixture
			//    would silently re-point every pin on it.
			const g = socket?.geometry;
			if (g?.region && Number.isFinite(g.plane)) {
				const r = g.region;
				const rect = (b) => ({ uMin: b.xMin, uMax: b.xMax, vMin: b.zMin, vMax: b.zMax });
				return {
					kind,
					region: rect(r),
					blocked: (Array.isArray(g.blocked) ? g.blocked : []).map(rect),
					regionExact: g.regionExact !== false,
					uvOrigin: XYZ(toWorld({ x: 0, y: g.plane, z: 0 })),
					uAxis:    XYZ(dirToWorld({ x: 1, y: 0, z: 0 })),
					vAxis:    XYZ(dirToWorld({ x: 0, y: 0, z: 1 })),
					normal:   XYZ(dirToWorld({ x: 0, y: 1, z: 0 })),
				};
			}
			// A general polygon ring — not what the mint emits today, kept because the frame maths is
			// domain-general and a non-rectangular region (ASK-116's merchant-drawn zones) needs it.
			const localRing = socket?.geometry?.polygon || socket?.geometry?.points;
			if (Array.isArray(localRing) && localRing.length >= 3) {
				// The (u,v) axes are derived in the FIXTURE's own frame and then rotated into world, so a
				// merchant's `{u,v}` pin means the same place on that table however the table is turned.
				// Deriving them from the world polygon instead would re-point every pin on a rotated
				// instance — the same class of defect as composing a domain by the anchor's yaw alone.
				const local = surfaceLocalFrame(localRing, frontLocal ? XYZ(frontLocal) : null);
				if (!local) return null;
				return {
					kind, points: localRing.map((p) => XYZ(toWorld(p))),
					normal:   XYZ(dirToWorld(local.normal)),
					uAxis:    XYZ(dirToWorld(local.uAxis)),
					vAxis:    XYZ(dirToWorld(local.vAxis)),
					uvOrigin: XYZ(toWorld(local.uvOrigin)),
				};
			}
			const size = socket?.geometry?.size;
			const c = toWorld(socket?.position || { x: 0, y: 0, z: 0 });
			return size ? { kind, position: XYZ(c), size } : null;
		}
		if (kind === "point") {
			const c = toWorld(socket?.position || { x: 0, y: 0, z: 0 });
			return { kind, position: { x: c.x, y: c.y, z: c.z } };
		}
		if (kind === "volume") {
			// ASK-139 F-139an — the mint emits a volume as its two DOMAIN corner empties, already composed
			// to FIXTURE-LOCAL metres (record-crate BIN_01: (−0.37, 0.47, −0.22) and (0.37, 0.85, 0.22)).
			// The box stays local and the fixture's own axes go to world beside it — the surface region's
			// convention — so the pure frame derives front/across/up from ONE transform, the body's.
			const pts = (socket?.geometry?.points || []).filter((p) => p && [p.x, p.y, p.z].every(Number.isFinite));
			if (pts.length < 2) return null;
			const lo = (k) => Math.min(...pts.map((p) => p[k])), hi = (k) => Math.max(...pts.map((p) => p[k]));
			const box = { xMin: lo("x"), xMax: hi("x"), yMin: lo("y"), yMax: hi("y"), zMin: lo("z"), zMax: hi("z") };
			// ASK-147 §14.4 — a volume leaning past 1° is published in the SOCKET's own frame: `points` are
			// socket-local corners, `origin` the socket's position and `normal`/`uAxis` its local +Y/+X, all
			// fixture-frame. They go to world with the body's own transform; `volumeFrame` derives the third
			// axis, so the box is built tilted rather than as the fixture-axis AABB of a rotated box. All three
			// or none: a level or yaw-only volume carries none and keeps today's domain byte-for-byte.
			const g = socket?.geometry;
			const finite = (v) => v && [v.x, v.y, v.z].every(Number.isFinite);
			if (finite(g?.normal) && finite(g?.uAxis) && finite(g?.origin)) {
				return { kind, box, uvOrigin: XYZ(toWorld(g.origin)), uAxis: XYZ(dirToWorld(g.uAxis)), normal: XYZ(dirToWorld(g.normal)) };
			}
			return {
				kind,
				box,
				uvOrigin: XYZ(toWorld({ x: 0, y: 0, z: 0 })),
				uAxis:    XYZ(dirToWorld({ x: 1, y: 0, z: 0 })),
				vAxis:    XYZ(dirToWorld({ x: 0, y: 0, z: 1 })),
				normal:   XYZ(dirToWorld({ x: 0, y: 1, z: 0 })),
			};
		}
		return null;
	}

	function quaternionFromBasis(basis) {
		const m = new THREE.Matrix4().makeBasis(V3(basis.x), V3(basis.y), V3(basis.z));
		return new THREE.Quaternion().setFromRotationMatrix(m);
	}

	/**
	 * Seat products on ONE socket of a placed fixture — the call both consumers make. kapowx's composer wraps
	 * it with its presentation extras (lane centre, hero extract, scene-truth regions); the console's inspector
	 * uses it as is, cloning a sample product at each slot.
	 *
	 * Returns `{ ok:false, reason }` when the grammar cannot draw the socket (a data problem to fix — never a
	 * guessed layout), else `{ ok:true, domain, approach, plan, slots, browse }`:
	 *   slots[i]  = { position: Vector3, quaternion: Quaternion, at, socketId, pinned, browse? } — in seat order,
	 *               for `products[plan.placements[i].index]`; `browse` only on a verb-bearing socket (`slotBrowse`);
	 *   browse    = the socket's derived verb (`browseOf`): null on a boot without one (r54), `refused` by name on a
	 *               word this client cannot play — the reviewer card's "cannot approve" reason (ASK-154 §2).
	 */
	function seatSocket({ socket, anchor, catalogueFrame = null, manifest = null, products = [], productTypes, maxItems = Infinity } = {}) {
		if (!socket || !anchor) return { ok: false, reason: "no socket or anchor" };
		const domain = resolveDomain({ socket, anchor, catalogueFrame, frontLocal: fixtureLocalFront(manifest) });
		const approachV = grammarApproach({ manifest, anchor, catalogueFrame, socket });
		const approach = { x: approachV.x, y: approachV.y, z: approachV.z };
		const plan = layoutByGrammar({
			socket, domain, approach, products, productTypes, maxItems,
			seed: `${anchor?.name || ""}:${socket.id || ""}`,
			pins: pinsFor({ products, anchorName: anchor?.name, socketId: socket.id }),
		});
		if (!plan.ok) return { ok: false, reason: plan.reason, domain, approach: approachV, plan };
		// ASK-154 seam 2 — a verb-bearing socket stamps every slot with its verb and its own frame (`slotBrowse`); a
		// socket without one adds NO key, so an r54 boot's slots are the bytes they always were.
		const stamp = slotBrowse(socket, { approach, frame: plan.frame });
		const slots = plan.placements.map((pl) => ({
			position: V3(pl.position), quaternion: quaternionFromBasis(pl.basis),
			at: pl.at ?? null, socketId: socket.id ?? null, pinned: pl.pinned === true,
			...(stamp ? { browse: stamp } : {}),
		}));
		return { ok: true, domain, approach: approachV, plan, slots, browse: browseOf(socket) };
	}

	return {
		makeToWorld, makeDirToWorld, grammarApproach, resolveDomain, quaternionFromBasis, seatSocket,
		fixtureLocalFront, socketLocalFront, pinsFor,
	};
}
